// EmailComposer form-link picker (Nate Swartz, 2026-08-28: "I want the
// ability to... add a form within the email window"). Lists the
// advisor's published company forms and, on pick, mints a link and
// hands the caller ready-to-insert HTML — a real hyperlink reading the
// form's title, not a bare URL for the advisor to paste and format
// themselves (the mess in Nate's screenshot).
//
// Link kind follows the composer's own context: when the composer
// already knows the household (the common case — most compose entry
// points pass one), the link is household-bound (personalized), so a
// submission comes back matched to the right family automatically and
// sensitive-field forms are safe to send. Without a household, a public
// link is minted instead; a form that collects SSN/bank/license data
// rejects that (server-enforced, same rule the Forms builder itself
// follows) and the rejection message is shown as-is — the fix is to
// send from a record that has a household attached.
//
// Pure JSX; the consumer (EmailComposer) decides where to insert the
// returned HTML.

import { useEffect, useState } from "react";

import { api, ApiError, type FormSummary } from "../../api";
import { Button, ErrorBanner } from "../../ui";

export interface FormLinkPickerProps {
  open: boolean;
  onClose: () => void;
  /** The household this compose is for, when known. Drives which link
   *  kind gets minted — see file header. */
  householdId: string | null;
  /** Ready-to-insert `<a href="...">Form Title</a>` HTML. */
  onInsert: (html: string) => void;
}

export function FormLinkPicker({
  open,
  onClose,
  householdId,
  onInsert,
}: FormLinkPickerProps) {
  const [forms, setForms] = useState<FormSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mintingSlug, setMintingSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listForms("company")
      .then((r) => {
        if (cancelled) return;
        setForms(r.forms.filter((f) => f.status === "live"));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : "Couldn't load forms.",
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  async function pick(form: FormSummary) {
    setError(null);
    setMintingSlug(form.slug);
    try {
      const result = await api.createFormLink(form.slug, {
        kind: householdId ? "personalized" : "public",
        ...(householdId ? { household_id: householdId } : {}),
      });
      onInsert(
        `<a href="${escapeAttribute(result.url)}">${escapeText(form.title)}</a>`,
      );
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't create the form link.",
      );
    } finally {
      setMintingSlug(null);
    }
  }

  return (
    <div
      className="template-picker form-link-picker"
      role="dialog"
      aria-label="Insert a form"
    >
      <header className="template-picker__header">
        <strong>Insert a form</strong>
        <button
          type="button"
          className="template-picker__close"
          aria-label="Close form picker"
          onClick={onClose}
        >
          ×
        </button>
      </header>
      {loading && (
        <div className="template-picker__loading">Loading forms…</div>
      )}
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {!loading && forms.length === 0 && !error && (
        <p className="template-picker__empty">
          No published forms yet. Build one in Forms first.
        </p>
      )}
      {!loading && forms.length > 0 && (
        <ul className="template-picker__list">
          {forms.map((f) => (
            <li key={f.slug} className="template-picker__item">
              <button
                type="button"
                className="template-picker__pick"
                disabled={mintingSlug !== null}
                onClick={() => void pick(f)}
              >
                <div className="template-picker__name">{f.title}</div>
                <div className="template-picker__meta">
                  {householdId
                    ? "Inserts a link tied to this household"
                    : "Inserts a shareable link"}
                  {mintingSlug === f.slug ? " — creating…" : ""}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <footer className="template-picker__footer">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </footer>
    </div>
  );
}

function escapeAttribute(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
